import { useEffect, useMemo, useRef, useState } from "react";

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import { MinuteStep, time } from "@/lib/time";
import { cn } from "@/lib/utils";

export interface TimeListProps {
  value: string;
  onSelect: (slotValue: string) => void;
  minuteStep?: MinuteStep;
  inputRef?: React.Ref<HTMLInputElement>;
  inputPlaceholder?: string;
  className?: string;
}

export const TimeList = ({
  value,
  onSelect,
  minuteStep = 15,
  inputRef,
  inputPlaceholder = "Type 3:15pm…",
  className,
}: TimeListProps) => {
  const [query, setQuery] = useState("");

  const slots = useMemo(() => time.generateSlots(minuteStep), [minuteStep]);
  const filtered = useMemo(
    () => slots.filter((slot) => time.matchesQuery(slot, query)),
    [slots, query],
  );

  // With a value, center it on open. With no value yet, center on the current
  // time (rounded to the step) so the user starts near now instead of 12:00 AM.
  const scrollTargetValue = value || time.nowRounded(minuteStep);

  const scrollTargetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handle = requestAnimationFrame(() => {
      scrollTargetRef.current?.scrollIntoView({ block: "center" });
    });
    return () => {
      cancelAnimationFrame(handle);
    };
  }, [value]);

  return (
    <Command shouldFilter={false} className={cn("rounded-none", className)}>
      <CommandInput
        ref={inputRef}
        placeholder={inputPlaceholder}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[280px]">
        <CommandEmpty>No times match</CommandEmpty>
        {filtered.map((slot) => {
          const selected = slot.value === value;
          return (
            <CommandItem
              key={slot.value}
              value={slot.value}
              ref={slot.value === scrollTargetValue ? scrollTargetRef : undefined}
              onSelect={() => {
                setQuery("");
                onSelect(slot.value);
              }}
              className={cn(selected && "bg-primary-100 text-primary-900")}
            >
              {slot.label}
            </CommandItem>
          );
        })}
      </CommandList>
    </Command>
  );
};
